/**
 * Feature Flags Configuration
 * Centralized feature flag management for controlled feature rollouts.
 *
 * Deployment mode (REACT_APP_DEPLOYMENT_MODE) drives most flags:
 *   - "cloud" (default): SaaS mode — billing, Google OAuth, R2 storage enabled
 *   - "onprem": Enterprise mode — billing disabled, local storage, all features unlocked
 */

const DEPLOYMENT_MODE = process.env.REACT_APP_DEPLOYMENT_MODE || 'cloud';

export const isOnPrem = () => DEPLOYMENT_MODE === 'onprem';
export const isCloud = () => DEPLOYMENT_MODE === 'cloud';

export const FEATURE_FLAGS = {
  // Logs Access Control
  LOGS_ACCESS_WARNING: true,          // Show warning banner for non-admin users
  LOGS_ACCESS_RESTRICTION: false,     // Actually restrict access (keep OFF initially)
  LOGS_REDIRECT_TO_PIPELINE: true,    // Redirect non-admins to pipeline logs

  // Future flags for gradual rollout
  LOGS_ADMIN_ONLY_MODE: false,        // Strict admin-only mode
  LOGS_ROLE_BASED_ACCESS: false,      // Role-based access control

  // Deployment mode: 'cloud' hides infra details (IPs, internal URLs), 'onprem' shows them
  CLOUD_MODE: isCloud(),

  // Deployment-derived flags
  BILLING_ENABLED: isCloud(),             // Razorpay/Paddle payment UI
  SUBSCRIPTIONS_ENABLED: isCloud(),       // Subscription page & plan enforcement
  GOOGLE_OAUTH_ENABLED: isCloud(),        // Google Sign-In button
  TRIAL_SYSTEM_ENABLED: isCloud(),        // Trial badges, expiration warnings
  FILE_EXPLORER_R2: isCloud(),            // Cloudflare R2 file storage
  FILE_EXPLORER_LOCAL: isOnPrem(),        // Local filesystem storage
};

/**
 * Get feature flag value with environment override support
 * @param {string} flagName - Feature flag name
 * @returns {boolean} - Flag value
 */
export const getFeatureFlag = (flagName) => {
  // Check for environment variable override first
  const envVar = `REACT_APP_${flagName}`;
  if (process.env[envVar] !== undefined) {
    return process.env[envVar] === 'true';
  }

  // Return default from FEATURE_FLAGS
  return FEATURE_FLAGS[flagName] || false;
};

/**
 * Check if feature is enabled
 * @param {string} flagName - Feature flag name
 * @returns {boolean} - True if feature is enabled
 */
export const isFeatureEnabled = (flagName) => {
  return getFeatureFlag(flagName);
};

/**
 * Get all feature flags (useful for debugging)
 * @returns {object} - All feature flags with their values
 */
export const getAllFeatureFlags = () => {
  const flags = {};
  Object.keys(FEATURE_FLAGS).forEach(key => {
    flags[key] = getFeatureFlag(key);
  });
  flags._deploymentMode = DEPLOYMENT_MODE;
  return flags;
};

/**
 * Hook for using feature flags in React components
 * @param {string} flagName - Feature flag name
 * @returns {boolean} - Flag value
 */
export const useFeatureFlag = (flagName) => {
  return getFeatureFlag(flagName);
};
